import angular from 'angular';
import {NOTIFICATION_TYPE_ERROR, NOTIFICATION_TYPE_WARNING} from '@/vue/store/modules/notifications';

angular.module('vrm.core').service('MfaService', MfaService);

/* @ngInject */
function MfaService(
    $rootScope,
    $http,
    ApiService,
    localStorageService,
    locale,
    UserService,
    InstallationsService,
    vrmNotificationsService,
) {
    const service = {
        // State
        promise: null,
        cancelToken: null,
        protectedAccessToken: null,
        mode: null,
        phone: null,
        // Getters
        get hasProtectedAccess() {
            return !!this.protectedAccessToken;
        },
        // Actions
        ensureMfaSettings,
        protectedAccessStart,
        protectedAccessVerify,
        dropProtectedAccess,
        passwordOnly,
        smsSetup,
        smsVerify,
        mobileSetup,
        mobileVerify,
    };

    $rootScope.$on('login', resetState);
    $rootScope.$on('logout', clearState);
    resetState();

    function clearState() {
        service.promise = null;
        service.cancelToken = null;
        service.protectedAccessToken = null;
        service.mfaMethod = null;
        service.phone = null;
    }

    function resetState() {
        clearState();
        if (ApiService.getUserId()) {
            ensureMfaSettings();
        }
    }

    function ensureMfaSettings() {
        return service.promise || fetchMfaSettings();
    }

    async function request(path, data = null) {
        const options = {
            skipResultChecks: true,
            headers: {},
            throwOnError: true,
        };
        if (!path.startsWith('/auth/') && service.protectedAccessToken) {
            options.skipAuthorization = true;
            options.headers['X-Authorization'] = `Bearer ${service.protectedAccessToken}`;
        }
        try {
            const response = await ApiService.call2(path, data, options);
            return response.data;
        } catch (error) {
            if (error.data) {
                return error.data;
            } else {
                return Promise.reject(error);
            }
        }
    }

    function requestForUser(path, data = null) {
        return request(`/users/${ApiService.getUserId()}/${path}`, data);
    }

    async function fetchMfaSettings() {
        const before = service.cancelToken = {};
        return service.promise = (async () => {
            try {
                const data = await requestForUser('mfa/state');
                if (before !== service.cancelToken) return;
                service.mode = data.mode;
                service.phone = data.phone;
            } catch {
                service.mode = null;
                service.phone = null;
                service.promise = null;
            }
        })();
    }

    let notificationId = null;
    function notifyError(message) {
        notificationId = vrmNotificationsService.add({
            type: NOTIFICATION_TYPE_ERROR,
            message,
        })
    }

    function notifyWarning(message) {
        notificationId = vrmNotificationsService.add({
            type: NOTIFICATION_TYPE_WARNING,
            message,
        })
    }

    async function protectedAccessStart() {
        vrmNotificationsService.remove(notificationId);
        try {
            const data = await request('/auth/protectedaccessstart', null);
            if (data.success) {
                return ProtectedAccessStartResult.createSuccess(data.mfaMode, data.tokenSent);
            } else {
                notifyError(data.message ? data.message : locale.getString('login.protected_access_start_failure'));
            }
        } catch (error) {
            notifyError(locale.getString('login.protected_access_start_failure'));
        }
        return ProtectedAccessStartResult.createFailure();
    }

    async function protectedAccessVerify(mfa_code) {
        vrmNotificationsService.remove(notificationId);
        try {
            const data = await request('/auth/protectedaccessverify', {mfa_code});
            if (data.success) {
                const token = data.token;
                service.protectedAccessToken = token;
                return ProtectedAccessVerifyResult.createSuccess(token);
            } else {
                notifyError(data.message ? data.message : locale.getString('login.protected_access_verify_failure'));
                return ProtectedAccessVerifyResult.createFailure();
            }
        } catch {
            notifyError(locale.getString('login.protected_access_verify_failure'));
        }
    }

    function dropProtectedAccess() {
        service.protectedAccessToken = null;
    }

    async function passwordOnly() {
        try {
            const {success} = await requestForUser('mfa/password-only');
            if (success) {
                service.mode = 'password';
                service.phone = null;
                UserService.reload();
                $rootScope.$broadcast('user:access_changed');
            } else {
                notifyError(locale.getString("mfa.password_only_failed"));
            }
            return success;
        } catch {
            notifyError(locale.getString("mfa.password_only_failed"));
            return false;
        }
    }

    async function smsSetup(phone, recaptcha_response) {
        try {
            const {success, errors} = await requestForUser('mfa/sms-setup', {phone, recaptcha_response});
            if (!success) {
                if (errors.includes('Phone number is invalid')) {
                    notifyWarning(locale.getString('mfa.sms_setup_invalid_phonenumber'));
                } else {
                    notifyError(locale.getString('mfa.sms_setup_failed'+ ' (1429)'));
                }
            }
            return success;
        } catch {
            notifyError(locale.getString('mfa.sms_setup_failed'));
            return false;
        }
    }

    async function smsVerify(phone, code) {
        try {
            const {success, phonenumber} = await requestForUser('mfa/sms-verify', {phone, code});
            if (success) {
                service.mode = 'sms';
                service.phone = phonenumber;
                UserService.reload();
                InstallationsService.reload();
                $rootScope.$broadcast('user:access_changed');
            } else {
                notifyWarning(locale.getString('mfa.sms_verify_failed'));
            }
            return success;
        } catch {
            notifyWarning(locale.getString('mfa.sms_verify_failed'));
            return false;
        }
    }

    async function mobileSetup(mode) {
        try {
            const data = await requestForUser('mfa/mobile-setup', {mode});
            if (!data.success) {
                notifyError(locale.getString('mfa.mobile_setup_failed'));
                return false;
            }
            return data;
        } catch {
            notifyError(locale.getString('mfa.mobile_setup_failed'));
            return false;
        }
    }

    async function mobileVerify(secret, code) {
        try {
            const {success} = await requestForUser('mfa/mobile-verify', {secret, code});
            if (success) {
                service.mode = 'mobile';
                service.phone = null;
                UserService.reload();
                InstallationsService.reload();
                $rootScope.$broadcast('user:access_changed');
            } else {
                notifyError(locale.getString('mfa.mobile_verify_failed'));
            }
            return success;
        } catch {
            notifyError(locale.getString('mfa.mobile_verify_failed'));
            return false;
        }
    }

    return service;
}

export class ProtectedAccessStartResult {
    /** @type Boolean */
    success = false;
    /** @type {'password'|'sms'|'mobile'} */
    mfaMode = 'password';
    /** @type Boolean */
    tokenSent = false;

    get tokenRequired() {
        return this.mfaMode !== 'password';
    }

    constructor(props) {
        Object.keys(props).forEach(key => this[key] = props[key]);
    }

    static createSuccess(mfaMode, tokenSent) {
        return new ProtectedAccessStartResult({
            success: true,
            mfaMode,
            tokenSent,
        });
    }

    static createFailure() {
        return new ProtectedAccessStartResult({
            success: false,
        });
    }
}

export class ProtectedAccessVerifyResult {
    /** @type Boolean */
    success = false;
    /** @type {String|null} */
    token = null;

    constructor(props) {
        Object.keys(props).forEach(key => this[key] = props[key]);
    }

    static createSuccess(token) {
        return new ProtectedAccessVerifyResult({success: true, token});
    }

    static createFailure() {
        return new ProtectedAccessVerifyResult({success: false});
    }
}
